import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore } from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { CONTAINER_KIND_PLURALS } from '@cerebro-diem/core';
import type { ContainerKind, ContainerRow } from '@cerebro-diem/core';
import { errorMessage } from '../../services/errors';
import { LIST_LIMIT, queryKeys, reachedLimit } from '../../services/queries';
import { containerPath, setArchived, useContainers } from '../../services/containers';
import { outlineButton } from '../../components/styles';

const KIND_ORDER: readonly ContainerKind[] = ['project', 'area', 'resource'];

/** Archived Projects, Areas and Resources, each with a way back. */
export default function ArchivePage() {
  const containersQuery = useContainers();
  const archived = (containersQuery.data ?? []).filter((c) => c.archived_at);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Archive className="w-8 h-8 text-primary" />
        <h1 className="text-2xl font-bold">Archive</h1>
      </div>
      <p className="text-sm text-on-surface-variant">
        Archived containers and everything in them are hidden from Today and the lists. Unarchive one to bring it back.
      </p>
      {reachedLimit(containersQuery.data) && (
        <p role="status" className="text-sm text-tertiary">
          Only your first {LIST_LIMIT.toLocaleString()} containers were read, so some archived ones may be missing.
        </p>
      )}

      {containersQuery.isError ? (
        <p className="text-sm text-error">Could not load the archive: {errorMessage(containersQuery.error)}</p>
      ) : containersQuery.isPending ? (
        <p className="text-sm text-on-surface-variant">Loading…</p>
      ) : archived.length === 0 ? (
        <p className="text-center py-12 text-on-surface-variant">Nothing is archived.</p>
      ) : (
        KIND_ORDER.filter((kind) => archived.some((c) => c.kind === kind)).map((kind) => (
          <section key={kind} className="space-y-2">
            <h2 className="text-lg font-semibold">{CONTAINER_KIND_PLURALS[kind]}</h2>
            <ul className="space-y-2">
              {archived
                .filter((c) => c.kind === kind)
                .map((container) => (
                  <ArchivedRow key={container.id} container={container} />
                ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

function ArchivedRow({ container }: { container: ContainerRow }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => setArchived(container.kind, container.id, false),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.containers }),
  });
  return (
    <li className="bg-surface rounded-xl p-4 shadow-sm flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-0">
        <Link to={containerPath(container.kind, container.id)} className="font-medium hover:underline break-words">
          {container.name}
        </Link>
        {container.archived_at && (
          <p className="text-xs text-on-surface-variant">Archived {format(new Date(container.archived_at), 'MMM d, yyyy')}</p>
        )}
        {mutation.isError && <p className="text-xs text-error">Not unarchived: {errorMessage(mutation.error)}</p>}
      </div>
      <button
        type="button"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className={`${outlineButton} inline-flex items-center gap-1`}
      >
        <ArchiveRestore className="w-4 h-4" /> {mutation.isPending ? 'Unarchiving…' : 'Unarchive'}
      </button>
    </li>
  );
}
